import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { IS_VIRTUAL_BG_BLUR_SUPPORTED } from 'features'
import config from 'config'
import { imagePath } from 'utils/image'
import { Body } from 'components/styled/typography'
import { Box } from 'components/styled/containers/box'
import { Flex } from 'components/styled/containers/flex'
import blurSlight from 'assets/blur_slight-optim.png'
import blurMore from 'assets/blur-optim.png'
import { Button } from 'components/styled/button'
import { VIRTUAL_BG_BLUR_AMOUNT } from 'app-constants'
import { I18nComponentType } from 'i18n-types'
import { connect } from 'react-redux'
import { mapLanguageToComponentProps } from 'i18n'
import { BACKGROUND_THUMBS, BG_IMAGE_NAMES } from './bg-images-handlers'
import { BodyStyle } from 'components/styled/typography/styles'

type SelectedType = {
  background: string
  blurAmount: number
}
interface VirtualBgProps {
  setSelected: (background?: string, blurAmount?: number) => void
  initSelected: SelectedType
  selectedBg?: string
  blur?: number
  i18n: I18nComponentType
  showPhotos?: boolean
  companyVideoBgImages?: { file: string; width: number; height: number }[]
}

const StyledImageContainer = styled.div<{ $selected: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  border-radius: ${({ theme }) => theme.space.s8};
  width: ${({ theme }) => theme.space.s80};
  height: ${({ theme }) => theme.space.s64};
  padding: ${({ theme }) => theme.space.s2};
  border: ${({ $selected, theme }) =>
    $selected
      ? `2px solid ${theme.color.primary110}`
      : `2px solid ${theme.color.grey07}`};
`

const StyledImage = styled.img`
  border-radius: ${({ theme }) => theme.space.s4};
  object-fit: cover;
  width: 100%;
  height: 100%;
`

const StyledInfoContainer = styled.div`
  margin-top: ${({ theme }) => theme.space.s4};
  margin-left: ${({ theme }) => theme.space.s8};
`

const StyledInfoText = styled.div`
  ${BodyStyle};
  font-weight: 600;
`

const StyledInfoButton = styled(Button)`
  display: inline-flex;
  margin-left: ${({ theme }) => theme.space.s8};
  align-items: center;
`

export const VirtualBg = ({
  setSelected,
  initSelected,
  selectedBg,
  blur = VIRTUAL_BG_BLUR_AMOUNT.small,
  i18n,
  showPhotos = true,
  companyVideoBgImages = [],
}: VirtualBgProps) => {
  const [endBgImageIndex, setEndBgImageIndex] = useState<number>(5)
  const [showInfo, setShowInfo] = useState<boolean>(false)

  const toggleShowBgImages = () => {
    if (endBgImageIndex === BACKGROUND_THUMBS.length - 1) {
      setEndBgImageIndex(5)
    } else {
      setEndBgImageIndex(BACKGROUND_THUMBS.length - 1)
    }
  }

  const updateSelected = ({
    background,
    blur = VIRTUAL_BG_BLUR_AMOUNT.small,
  }: {
    background: string
    blur?: number
  }) => {
    setSelected(background, blur)
  }

  useEffect(() => {
    updateSelected(initSelected)
  }, [])

  return (
    <>
      <Box mt="s12">
        <Flex flexDirection="column" gap="s8">
          <StyledInfoText>
            {i18n.virtualBg.infoText1}
            <StyledInfoButton
              variant="secondary"
              onClick={() => setShowInfo(!showInfo)}
              size="small"
              icon="help"
            />
          </StyledInfoText>
          {showInfo && (
            <StyledInfoContainer>
              <Body>{i18n.virtualBg.infoText2}</Body>
              <Body>{i18n.virtualBg.infoText3}</Body>
            </StyledInfoContainer>
          )}
        </Flex>
      </Box>
      {showPhotos && (
        <Flex gap="s16" flexWrap="wrap" mt="s16" mb="s12">
          {config.SHOW_BLUR_FOR_VIRTUAL_BG && IS_VIRTUAL_BG_BLUR_SUPPORTED && (
            // images coming for these too, text just placeholder
            <>
              <StyledImageContainer
                $selected={
                  selectedBg === 'blur' && blur === VIRTUAL_BG_BLUR_AMOUNT.small
                }
                onClick={() =>
                  updateSelected({
                    background: 'blur',
                    blur: VIRTUAL_BG_BLUR_AMOUNT.small,
                  })
                }
              >
                <StyledImage
                  src={blurSlight}
                  alt="slightly blurred video background"
                />
              </StyledImageContainer>
              <StyledImageContainer
                $selected={
                  selectedBg === 'blur' && blur === VIRTUAL_BG_BLUR_AMOUNT.large
                }
                onClick={() =>
                  updateSelected({
                    background: 'blur',
                    blur: VIRTUAL_BG_BLUR_AMOUNT.large,
                  })
                }
              >
                <StyledImage src={blurMore} alt="blurred video background" />
              </StyledImageContainer>
            </>
          )}
          {companyVideoBgImages.map((image, index) => {
            return (
              <StyledImageContainer
                key={image.file}
                $selected={selectedBg === image.file}
                onClick={() => updateSelected({ background: image.file })}
              >
                <StyledImage
                  src={imagePath(image)}
                  alt="company video background"
                />
              </StyledImageContainer>
            )
          })}
          {BACKGROUND_THUMBS.slice(0, endBgImageIndex).map(
            (imageSrc, index) => {
              return (
                <StyledImageContainer
                  key={imageSrc}
                  $selected={selectedBg === BG_IMAGE_NAMES[index]}
                  onClick={() =>
                    updateSelected({ background: BG_IMAGE_NAMES[index] })
                  }
                >
                  <StyledImage src={imageSrc} alt="video background" />
                </StyledImageContainer>
              )
            }
          )}
          <Button
            variant="basic"
            icon={
              endBgImageIndex !== BACKGROUND_THUMBS.length - 1
                ? 'expand_more'
                : 'expand_less'
            }
            onClick={() => toggleShowBgImages()}
            text={
              endBgImageIndex !== BACKGROUND_THUMBS.length - 1
                ? i18n.virtualBg.showMoreButtonText
                : i18n.virtualBg.showLessButtonText
            }
          />
        </Flex>
      )}
    </>
  )
}

export default connect(mapLanguageToComponentProps())(VirtualBg)
